import {
  useCreateOrganisationNominal,
  useDeleteOrganisationNominal,
  useOrganisationNominals,
  useUpdateOrganisationNominal,
} from '@my/directory-api/hooks';
import type {
  BaseStatusValue,
  NominalTypeValue,
  OrganisationNominal,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  SelectInput,
  Switch,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface NominalCodesSectionProps {
  /** Owning organisation id; the panel stays disabled while this is null. */
  organisationId: string | null;
  /**
   * Organisation's own country ISO code. Stamped on every nominal code
   * created from this card — chart-of-accounts entries belong to a single
   * jurisdiction, so the country isn't user-editable in the form. `null`
   * while the org payload hasn't loaded.
   */
  organisationCountryIsoCode: string | null;
}

/**
 * Wire-value list (no labels — translated at render time so the dropdown
 * tracks language switches without restating the catalogue here).
 */
const NOMINAL_TYPE_VALUES: NominalTypeValue[] = ['Sales', 'Purchase'];

/**
 * Bottom section on the Organisation page. Lists nominal codes (chart of
 * accounts) for the org and allows in-place CRUD via the dedicated
 * `/organisationNominals` endpoints. Layout mirrors TaxSchemasSection — same
 * table shape, same inline-editor row, same delete-confirmation flow.
 *
 * Field semantics (best understood from the BE entity + the legacy TMS UI):
 *  - `code`        — short FE-facing identifier (e.g. `SALES-A`, `4000`).
 *  - `description` — human-readable label shown to operators.
 *  - `type`        — `Sales` or `Purchase` (singular, mirrors the BE enum).
 *  - `value`       — raw GL number / external accounting-system identifier
 *                    (e.g. the Xero / Sage account code). Wired through to
 *                    the integration layer; the FE just preserves it.
 *
 * FE validation mirrors the BE FluentValidation rules:
 *  - Code: required, max 64 chars
 *  - Description: required, max 255 chars
 *  - Value: required, max 128 chars
 *  - CountryIsoCode: required, exactly 2 chars (always satisfied via the org)
 */
export function NominalCodesSection({
  organisationId,
  organisationCountryIsoCode,
}: NominalCodesSectionProps) {
  const { t } = useTranslation('organisation');
  const listQuery = useOrganisationNominals(organisationId);
  const createMutation = useCreateOrganisationNominal();
  const updateMutation = useUpdateOrganisationNominal();
  const deleteMutation = useDeleteOrganisationNominal();

  // `null` = no editor open; `'new'` = drafting a brand-new code; a guid
  // = editing the row with that id. Edit renders in-place; +Add at the top.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<NominalCodeForm>(emptyForm(organisationCountryIsoCode));
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OrganisationNominal | null>(null);

  // Ref to the currently-mounted editor `<tr>` — only one renders at a
  // time (`'new'` at the top OR the edited row in-place) so the ref always
  // points at whichever editor is open. Used to scroll the editor into
  // view when it opens, so clicking edit on the last row doesn't leave
  // the editor hidden below the scrollable area.
  const editorRowRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (editingId === null) return;
    // requestAnimationFrame defers the scroll until after the editor row
    // has actually mounted/painted — without this, `scrollIntoView` runs
    // against a stale layout and can no-op or land in the wrong spot.
    const id = requestAnimationFrame(() => {
      editorRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const startEdit = (row: OrganisationNominal) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm(organisationCountryIsoCode));
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm(organisationCountryIsoCode));
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft)) return;
    const parsedOrder = Number.parseInt(draft.displayOrder, 10);
    const displayOrder = Number.isFinite(parsedOrder) ? parsedOrder : 0;

    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync({
          organisationId,
          code: draft.code.trim(),
          description: draft.description.trim(),
          type: draft.type,
          value: draft.value.trim(),
          displayOrder,
          countryIsoCode: draft.countryIsoCode.trim().toUpperCase(),
          status: draft.status,
        });
        showToast({ type: 'success', message: t('nominalCodes.added') });
      } else {
        await updateMutation.mutateAsync({
          organisationId,
          body: {
            nominalId: editingId,
            code: draft.code.trim(),
            description: draft.description.trim(),
            type: draft.type,
            value: draft.value.trim(),
            displayOrder,
            countryIsoCode: draft.countryIsoCode.trim().toUpperCase(),
            status: draft.status,
          },
        });
        showToast({ type: 'success', message: t('nominalCodes.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('nominalCodes.saveFailed'),
      });
    }
  };

  const requestDelete = (row: OrganisationNominal) => setPendingDelete(row);

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('nominalCodes.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('nominalCodes.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Hide the +Add header button while an editor is open — there's no sense
  // in starting a new draft when one is already in flight.
  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('nominalCodes.addAria')}
        onClick={startNew}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('nominalCodes.add')}
      </button>
    ) : null;

  return (
    <FormSection title={t('nominalCodes.title')} columns={1} actions={headerAction}>
      {listQuery.isPending && <Loading label={t('nominalCodes.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('nominalCodes.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && listQuery.data.length === 0 && editingId !== 'new' && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
          <p className="text-sm text-slate-600">{t('nominalCodes.empty')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('nominalCodes.emptyHint')}</p>
        </div>
      )}

      {listQuery.isSuccess && (listQuery.data.length > 0 || editingId === 'new') && (
        // Two nested wrappers:
        //  - outer clips the rounded corners so the dark header fills them
        //  - inner enables horizontal scroll on narrow screens (mobile etc.)
        // Both are needed because `overflow-x-auto` and `overflow-hidden` on
        // the same element conflict, and tables don't honour border-radius
        // directly.
        <div className="overflow-hidden rounded-md">
          {/*
            Cap the scroll viewport at ~60% of the viewport height so the
            horizontal scrollbar always lands inside the visible area —
            without the cap, on mobile you'd have to scroll the *page* down
            past every row to reach the bottom-edge scrollbar.
          */}
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full min-w-[720px] table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('nominalCodes.table.country')}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('nominalCodes.table.code')}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('nominalCodes.table.type')}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('nominalCodes.table.value')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('nominalCodes.table.description')}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t('nominalCodes.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500">
                {/*
                  New-code draft renders at the TOP of the table as a single
                  colspan row hosting the half-width editor. Edits render
                  IN-PLACE further down — the same colspan trick swaps that
                  row's regular cells for the editor.
                */}
                {editingId === 'new' && (
                  <tr ref={editorRowRef}>
                    <td colSpan={6} className="bg-slate-50 p-3">
                      <div className="max-w-2xl">
                        <NominalCodeForm
                          draft={draft}
                          onChange={setDraft}
                          submitted={submitted}
                          onSave={handleSave}
                          onCancel={cancel}
                          isBusy={isBusy}
                        />
                      </div>
                    </td>
                  </tr>
                )}

                {listQuery.data.map((row) => {
                  if (editingId === row.id) {
                    return (
                      <tr key={row.id} ref={editorRowRef}>
                        <td colSpan={6} className="bg-slate-50 p-3">
                          <div className="max-w-2xl">
                            <NominalCodeForm
                              draft={draft}
                              onChange={setDraft}
                              submitted={submitted}
                              onSave={handleSave}
                              onCancel={cancel}
                              onDelete={() => requestDelete(row)}
                              isBusy={isBusy}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  // While another row is being edited (or +Add is open),
                  // dim other rows and disable double-click — same lock
                  // behaviour as Cargo Definitions / Charges & Costs so
                  // an unsaved draft can't be silently dropped.
                  const otherEditing = editingId !== null;
                  return (
                    <tr
                      key={row.id}
                      className={
                        otherEditing
                          ? 'opacity-50'
                          : 'cursor-pointer hover:bg-slate-50'
                      }
                      onDoubleClick={otherEditing ? undefined : () => startEdit(row)}
                    >
                      <td className="px-3 py-0.5 text-center text-slate-700">
                        {row.countryIsoCode}
                      </td>
                      <td className="px-3 py-0.5 text-center font-medium text-slate-900">
                        {row.code}
                      </td>
                      <td className="px-3 py-0.5 text-center text-slate-700">
                        {row.type === 'Sales'
                          ? t('nominalCodes.types.sales')
                          : t('nominalCodes.types.purchase')}
                      </td>
                      <td className="px-3 py-0.5 text-center text-slate-700">
                        {row.value || '—'}
                      </td>
                      <td className="px-3 py-0.5 text-left text-slate-600">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{row.description}</span>
                          {row.status === 'Inactive' && <InactiveBadge />}
                        </div>
                      </td>
                      <td className="px-3 py-0.5 text-right">
                        <button
                          type="button"
                          aria-label={t('nominalCodes.ariaEdit', { code: row.code })}
                          onClick={() => startEdit(row)}
                          disabled={otherEditing}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('nominalCodes.deleteDialog.title')}
        message={
          pendingDelete
            ? t('nominalCodes.deleteDialog.message', {
                code: pendingDelete.code,
                description: pendingDelete.description,
              })
            : null
        }
        confirmLabel={t('nominalCodes.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function InactiveBadge() {
  const { t } = useTranslation('organisation');
  return (
    <span className="inline-flex items-center rounded-sm bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
      {t('nominalCodes.inactiveBadge')}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Form
// ────────────────────────────────────────────────────────────────────────────

interface NominalCodeForm {
  code: string;
  description: string;
  type: NominalTypeValue;
  value: string;
  countryIsoCode: string;
  displayOrder: string; // string for input; parsed on save
  status: BaseStatusValue;
}

function emptyForm(organisationCountryIsoCode: string | null): NominalCodeForm {
  // Country is always the organisation's own country — a chart of accounts
  // belongs to a single jurisdiction. Falls back to 'IE' only if the org
  // payload hasn't loaded yet (very early render).
  // The organisation's own country, same as the tax schedule — IE, GB and XI
  // are kept distinct all the way down rather than collapsed into each other.
  const country = (organisationCountryIsoCode ?? 'IE').trim().toUpperCase();
  return {
    code: '',
    description: '',
    type: 'Sales',
    value: '',
    countryIsoCode: country,
    displayOrder: '0',
    status: 'Active',
  };
}

function toForm(row: OrganisationNominal): NominalCodeForm {
  return {
    code: row.code,
    description: row.description,
    type: row.type,
    value: row.value,
    countryIsoCode: row.countryIsoCode,
    displayOrder: String(row.displayOrder ?? 0),
    status: row.status,
  };
}

function isFormValid(form: NominalCodeForm): boolean {
  return (
    form.code.trim().length > 0 &&
    form.code.length <= 64 &&
    form.description.trim().length > 0 &&
    form.description.length <= 255 &&
    form.value.trim().length > 0 &&
    form.value.length <= 128 &&
    form.countryIsoCode.trim().length === 2
  );
}

interface NominalCodeFormProps {
  draft: NominalCodeForm;
  onChange: (next: NominalCodeForm) => void;
  submitted: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isBusy: boolean;
}

function NominalCodeForm({
  draft,
  onChange,
  submitted,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: NominalCodeFormProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof NominalCodeForm>(key: K, value: NominalCodeForm[K]) =>
    onChange({ ...draft, [key]: value });

  const typeOptions = NOMINAL_TYPE_VALUES.map((value) => ({
    value,
    label: value === 'Sales' ? t('nominalCodes.types.sales') : t('nominalCodes.types.purchase'),
  }));

  const errs = {
    code: !draft.code.trim(),
    description: !draft.description.trim(),
    value: !draft.value.trim(),
  };

  // `countryIsoCode` is intentionally not surfaced in the editor — it's
  // preserved in form state (set on emptyForm / preserved on edit) and
  // stamped on the wire, but a chart of accounts is internal-org
  // bookkeeping rather than jurisdictional, so the operator doesn't need
  // to think about it. The table column carries the country for any
  // audit/curiosity case.

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label={t('nominalCodes.fields.code')}
          name="code"
          autoComplete="off"
          value={draft.code}
          maxLength={64}
          onChange={(e) => set('code', e.target.value)}
          error={submitted && errs.code}
          errorMessage={t('nominalCodes.validation.codeRequired')}
          placeholder={t('nominalCodes.fields.codePlaceholder')}
        />
        <SelectInput
          label={t('nominalCodes.fields.type')}
          name="type"
          value={draft.type}
          onChange={(e) => set('type', e.target.value as NominalTypeValue)}
          options={typeOptions}
        />
        <TextInput
          label={t('nominalCodes.fields.description')}
          name="description"
          autoComplete="off"
          value={draft.description}
          maxLength={255}
          onChange={(e) => set('description', e.target.value)}
          error={submitted && errs.description}
          errorMessage={t('nominalCodes.validation.descriptionRequired')}
        />
        <TextInput
          label={t('nominalCodes.fields.value')}
          name="value"
          autoComplete="off"
          value={draft.value}
          maxLength={128}
          onChange={(e) => set('value', e.target.value)}
          error={submitted && errs.value}
          errorMessage={t('nominalCodes.validation.valueRequired')}
          placeholder={t('nominalCodes.fields.valuePlaceholder')}
        />
        {/*
          `displayOrder` is intentionally NOT exposed as an input — order
          will be controlled from the row itself via up/down move actions
          (planned). The form preserves the existing value on edit and
          defaults to 0 on create so the BE keeps a stable sort key.
        */}
        <Switch
          label={t('nominalCodes.fields.status')}
          name="status"
          checked={draft.status === 'Active'}
          enabledLabel={t('nominalCodes.fields.statusActive')}
          disabledLabel={t('nominalCodes.fields.statusInactive')}
          onChange={(e) => set('status', e.currentTarget.checked ? 'Active' : 'Inactive')}
        />
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('nominalCodes.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('nominalCodes.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('nominalCodes.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
